import useFetchResource from 'hooks/useFetchResource';
import api from 'libs/api';
import { NavMenuItem } from 'pages/App/Header/Menu/WebsiteMenu/styled';
import { ICustomPage } from 'pages/Website/CustomPage';
import React, { useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import styled from 'styled-components';
import useProfile from 'hooks/useProfile';
import useAccount from 'hooks/useAccount';
import { IAccount } from 'domain/env/types';
import { getIn } from 'formik';
import { fullUrlPath } from 'libs/utils';
import { URL_TO_REQUESTS_LIST, URL_TO_FORUM_LIST, URL_TO_AMENITIES_LIST } from 'libs/constants';

interface IUseWebsiteMenu {
  toggle?: () => void;
}

interface ICustomSlugPath {
  [key: string]: string;
}

export const generatePathFromSlug = (
  slug: string,
  isAuthorized?: boolean,
  canRestoreAmenity?: boolean,
  account?: IAccount
) => {
  if (slug === 'home') return '/';

  if (slug === 'parking') {
    const parkingPath = account?.parking_reservation_guid
      ? `/parking/reservation/${account.parking_reservation_guid}`
      : '';

    return parkingPath ? fullUrlPath(process.env.REACT_APP_APP_DOMAIN!, parkingPath) : '/sign-in';
  }

  // Authorized feature pages; signed-out visitors go to sign-in and return here via redirect_to.
  const authorizedTargets: ICustomSlugPath = {
    amenities: canRestoreAmenity ? URL_TO_AMENITIES_LIST : '/amenities',
    forum: URL_TO_FORUM_LIST,
    calendar: '/events',
    faq: '/faq',
    surveys: '/surveys',
    requests: URL_TO_REQUESTS_LIST,
  };

  const target = authorizedTargets[slug];

  if (target) {
    return isAuthorized ? target : `/sign-in?redirect_to=${encodeURIComponent(target)}`;
  }

  return `/public/${slug}`;
};

const useWebsiteMenu = ({ toggle = () => null }: IUseWebsiteMenu = {}): {
  renderItems: () => JSX.Element;
} => {
  const { isAuthorized } = useAuth();
  const { profile } = useProfile();
  const { account } = useAccount();
  const canRestoreAmenity = useMemo(() => {
    return getIn(profile, ['additional_permissions', 'can_restore_amenity']);
  }, [profile]);

  const { resource: customPages } = useFetchResource<ICustomPage[]>({
    api: api.website.public.getAll,
    initialValues: [],
    serializer: (data) => data.resources,
  });

  const menuItems = useMemo(
    () =>
      customPages
        .filter(
          ({ policy: { can_read }, slug }) =>
            can_read && !(process.env.REACT_APP_HIDE_MANAGE_PLAN && slug === 'parking')
        )
        .map(({ slug, title: label }) => ({
          slug,
          exact: slug === 'home',
          label,
          path: generatePathFromSlug(slug, isAuthorized, canRestoreAmenity, account),
        })),
    [customPages, isAuthorized, canRestoreAmenity, account]
  );

  const renderItems = useCallback(
    () => (
      <MenuItems>
        {menuItems.map((item, index) => (
          <MenuItem
            key={`${item.slug}-${index}`}
            exact={item.exact}
            onClick={toggle}
            to={item.path}
            as={NavLink}
          >
            {item.label}
          </MenuItem>
        ))}
      </MenuItems>
    ),
    [menuItems, toggle]
  );

  return { renderItems };
};

export default useWebsiteMenu;

const MenuItems = styled.div`
  padding: 15px 45px;
`;

const MenuItem = styled(NavMenuItem)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
